<template>
  <v-col cols="12">
    <v-row no-gutters class="mb-2">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <template v-if="!hideTitle">
          <subtitle-component>
            {{ businessAddressesTitle }}
          </subtitle-component>
        </template>
        <div class="flex-grow-1">
          <slot name="pagination"></slot>
        </div>
      </v-col>
    </v-row>

    <GoogleMyBusinessData
      :businessAddresses="businessAddresses"
      :panelIndex="panelIndex"
    >
      <template v-slot="{ data }">
        <div class="position-relative">
          <v-card class="mb-4 pr-lg-3 pr-xl-0">
            <v-row no-gutters>
              <v-col cols="12" lg="4">
                <v-row no-gutters class="py-4 pb-lg-0">
                  <v-col cols="12">
                    <slot name="gmbSearch"></slot>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" lg="8">
                <v-row no-gutters align="center" class="px-5">
                  <v-col cols="5" xl="4" class="ml-0 ml-xl-auto py-4">
                    <v-select
                      :label="$t('message.common.sortBy')"
                      :items="sortItems"
                      hide-details
                      v-model="selectedSortItem"
                      return-object
                      item-text="name"
                    ></v-select>
                  </v-col>
                  <v-col cols="5" xl="3" class="px-4">
                    <v-select
                      :label="$t('message.common.direction')"
                      :items="sortDirection"
                      hide-details
                      return-object
                      item-text="name"
                      v-model="selectedSortDirection"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="2"
                    sm="2"
                    align-self="center"
                    class="pt-2 px-2 pr-xl-0 text-right"
                  >
                    <ui-button
                      icon="mdi-sort"
                      color="primary"
                      @click="handleSortClick"
                    >
                      {{ $t("message.common.sort") }}
                    </ui-button>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card>

          <template v-if="businessAddresses.length > 0">
            <v-expansion-panels class="rounded" flat v-model="panelIndex">
              <template v-for="item in data">
                <ExpansionPanel
                  :facilityId="
                    item &&
                      item.businessAddress &&
                      item.businessAddress.facility_id
                  "
                  :gmbAddressId="
                    item && item.businessAddress && item.businessAddress.id
                  "
                  :itemData="item && item.data && item.data.data"
                  :isEmpty="isEmpty(item.data)"
                  :key="item.businessAddress.id"
                >
                  <template #header>
                    <v-row no-gutters>
                      <v-col cols="6">
                        <BusinessAddress
                          :name="item.businessAddress.name"
                          :address="item.businessAddress.formatted_address"
                        />
                      </v-col>
                      <template v-if="!isEmpty(item.data)">
                        <v-col cols="3">
                          <Rate
                            :average="item.data.score.last_value"
                            :info="$t('message.visibility.GMBSummary.rate')"
                          />
                          <slot name="rate"></slot>
                        </v-col>
                        <v-col cols="3">
                          <LastAudit :lastAudit="item.data.score.diff" />
                        </v-col>
                      </template>
                      <template v-else>
                        <v-col cols="6" class="d-flex align-center">
                          <v-icon>mdi-database-search</v-icon>
                          <span>{{ $t("message.common.collectData") }}</span>
                        </v-col>
                      </template>
                    </v-row>
                  </template>
                  <template v-slot:content="{ buttonsInfo }">
                    <v-row no-gutters>
                      <v-col cols="12">
                        <v-tabs
                          v-model="item.tab"
                          centered
                          slider-color="orange"
                          @change="itemTabChange(item, $event)"
                        >
                          <v-tab>{{ $t("message.common.summary") }}</v-tab>
                          <v-tab>{{ $t("message.common.history") }}</v-tab>
                        </v-tabs>
                        <v-tabs-items v-model="item.tab">
                          <v-tab-item>
                            <GMBIndicatorsData :audit="item.data">
                              <template v-slot="{ formattedIndicators }">
                                <v-row no-gutters class="pa-4">
                                  <v-col col="6">
                                    <Indicator
                                      class="px-5"
                                      :indicator="
                                        formattedIndicators.reviewsRating
                                      "
                                    />
                                    <Indicator
                                      class="px-5"
                                      :indicator="
                                        formattedIndicators.avgRateOnMainPage
                                      "
                                    />
                                    <Indicator
                                      class="px-5"
                                      :indicator="formattedIndicators.reviewers"
                                    />
                                    <Indicator
                                      class="px-5"
                                      :indicator="
                                        formattedIndicators.numberOfPhotos
                                      "
                                    />
                                  </v-col>
                                  <v-col col="6">
                                    <Indicator
                                      class="px-5"
                                      :indicator="formattedIndicators.address"
                                    />
                                    <Indicator
                                      class="px-5"
                                      :indicator="
                                        formattedIndicators.phoneNumber
                                      "
                                    />
                                    <Indicator
                                      class="px-5"
                                      :indicator="formattedIndicators.website"
                                    />
                                    <Indicator
                                      class="px-5"
                                      :indicator="formattedIndicators.openHours"
                                    />
                                  </v-col>
                                </v-row>
                              </template>
                            </GMBIndicatorsData>
                          </v-tab-item>
                          <v-tab-item>
                            <ChartData
                              :businessAddress="item.businessAddress"
                              :selectedChartItem="item.selectedChartItem"
                              :month="item.paginationMonth"
                            >
                              <template
                                v-slot="{
                                  chartData,
                                  items,
                                  select,
                                  currentMonth,
                                  chartLoading
                                }"
                              >
                                <v-row no-gutters class="pa-4">
                                  <v-col cols="4">
                                    <v-select
                                      :items="items"
                                      :value="select"
                                      item-text="label"
                                      item-value="value"
                                      return-object
                                      @input="item.selectedChartItem = $event"
                                      :label="$t('message.common.pointer')"
                                    ></v-select>
                                  </v-col>
                                  <v-col cols="12">
                                    <div>
                                      <template
                                        v-if="
                                          chartData.data.length > 0 &&
                                            chartData.labels.length > 0
                                        "
                                      >
                                        <LineChart
                                          :height="400"
                                          :data="chartData.data"
                                          :labels="chartData.labels"
                                          :scale="select.scale"
                                          :key="select.value"
                                        />
                                      </template>
                                      <template v-else>
                                        <v-row no-gutters>
                                          <v-col
                                            cols="12"
                                            class="d-flex justify-center align-center flex-column"
                                            style="height: 400px;"
                                          >
                                            <v-icon
                                              x-large
                                              class="ma-2"
                                              color="primary"
                                            >
                                              mdi-database-search
                                            </v-icon>
                                            <h3>
                                              {{ $t("message.common.noData") }}
                                            </h3>
                                          </v-col>
                                        </v-row>
                                      </template>
                                      <SectionLoader :status="chartLoading" />
                                    </div>
                                    <ChartPagination
                                      @changeMonth="
                                        item.paginationMonth = $event
                                      "
                                      :currentMonth="currentMonth"
                                      componentName="locations_quality"
                                    />
                                  </v-col>
                                </v-row>
                              </template>
                            </ChartData>
                          </v-tab-item>
                        </v-tabs-items>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <div class="d-flex justify-center">
                          <ButtonWithInfoText
                            v-for="info in buttonsInfo"
                            :key="info.id"
                            :to="info.to"
                            :info-text="info.text"
                            :label="info.label"
                            class="button-info-text"
                          />
                        </div>
                      </v-col>
                    </v-row>
                  </template>
                </ExpansionPanel>
              </template>
            </v-expansion-panels>
          </template>
          <template v-else>
            <v-row no-gutters>
              <v-col
                cols="12"
                class="d-flex justify-center align-center flex-column"
                style="height: 200px;"
              >
                <v-icon x-large class="ma-2" color="primary">
                  mdi-database-search
                </v-icon>
                <h3>{{ $t("message.common.noData") }}</h3>
              </v-col>
            </v-row>
          </template>
          <SectionLoader :status="loading" />
        </div>
      </template>
    </GoogleMyBusinessData>
  </v-col>
</template>

<script>
// Data
import GoogleMyBusinessData from "@/components/data/Visibility/GoogleMyBusiness.js";
import GMBIndicatorsData from "@/components/data/Visibility/GoogleMyBusinessIndicators.js";
import ChartData from "@/components/data/Visibility/GoogleMyBusinessChart.js";

//UI
import ExpansionPanel from "@/components/ui/ExpansionPanel/ExpansionPanel.vue";
import BusinessAddress from "@/components/ui/ExpansionPanel/Header/BusinessAddress.vue";
import Indicator from "@/components/ui/ExpansionPanel/Content/Info/Indicator.vue";
import Rate from "@/components/ui/ExpansionPanel/Header/Rate.vue";
import LastAudit from "@/components/ui/ExpansionPanel/Header/LastAudit.vue";
import ChartPagination from "@/components/Chart/Pagination.vue";
import LineChart from "@/components/ui/ExpansionPanel/Content/History/chart.js";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import SubtitleComponent from "@/components/ui/Subtitle/Subtitle.component.vue";
import ButtonWithInfoText from "@/components/ui/ExpansionPanel/Content/Action/ButtonWithInfoText.vue";

import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import isEmpty from "lodash/isEmpty";

export default {
  name: "GoogleMyBusinessHOC",
  components: {
    BusinessAddress,
    ChartData,
    ChartPagination,
    ExpansionPanel,
    GoogleMyBusinessData,
    Indicator,
    GMBIndicatorsData,
    LastAudit,
    LineChart,
    Rate,
    SectionLoader,
    SubtitleComponent,
    ButtonWithInfoText
  },
  props: {
    hideTitle: {
      type: Boolean,
      default: false
    },
    businessAddresses: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    sort: {
      type: String,
      default: "desc"
    }
  },
  data() {
    return {
      businessAddressesTitle: this.$t(
        "message.businessAddresses.quality.viewTitle"
      ),
      isEmpty: isEmpty,
      selectedSortItem: {
        name: this.$t(
          "message.visibility.GMBSummary.history.select.centerRate"
        ),
        value: "score"
      },
      selectedSortDirection: {
        name: this.$t("message.common.descending"),
        value: "desc"
      },
      sortItems: [
        {
          name: this.$t(
            "message.visibility.GMBSummary.history.select.centerRate"
          ),
          value: "score"
        },
        {
          name: this.$t(
            "message.visibility.GMBSummary.history.select.googleMapsRatingQty"
          ),
          value: "reviewers"
        },
        {
          name: this.$t(
            "message.visibility.GMBSummary.history.select.googleMapsRate"
          ),
          value: "reviews_rating"
        }
        // {
        //   name: this.$t(
        //     "message.visibility.GMBSummary.history.select.profileCompletness"
        //   ),
        //   value: "profile_completeness"
        // }
      ],
      sortDirection: [
        {
          name: this.$t("message.common.descending"),
          value: "desc"
        },
        {
          name: this.$t("message.common.ascending"),
          value: "asc"
        }
      ],
      panelIndex: undefined
    };
  },

  async created() {
    this.selectedSortDirection = this.sortDirection.find(
      ({ value }) => value === this.sort
    );
    await this.handleSortClick();
  },

  methods: {
    handleSortClick() {
      const payload =
        this.selectedSortDirection.value === "desc"
          ? `-${this.selectedSortItem.value}`
          : this.selectedSortItem.value;

      this.$emit("sort", payload);
    },
    itemTabChange(item, tabIndex) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATIONS_QUALITY_ITEM_ACTION,
        location_id: item.businessAddress.id,
        action: tabIndex === 0 ? "tab_summary" : "tab_history"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.button-info-text {
  width: 100%;

  &:not(:last-child) {
    border-right: 1px solid rgba($gray-light, 0.3);
  }
}
</style>
